import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getApiUrl } from '@/config/api';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FeatureFlagContextType {
    /** Check whether a single flag is enabled */
    isEnabled: (flag: string) => boolean;
    /** Full map of flag → boolean */
    flags: Record<string, boolean>;
    /** Current resolved environment */
    environment: string;
    /** True while fetching flags from backend */
    loading: boolean;
    /** Error message if fetch failed */
    error: string | null;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
    children: ReactNode;
}

/**
 * Provides feature-flag state to the component tree.
 *
 * Flags are fetched from GET /api/feature-flags/ on mount. The backend is the
 * single source of truth — no need to duplicate the registry in the frontend.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
    const [flags, setFlags] = useState<Record<string, boolean>>({});
    const [environment, setEnvironment] = useState<string>('local');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchFlags() {
            try {
                const res = await fetch(getApiUrl('/api/feature-flags/'), {
                    headers: { Accept: 'application/json' },
                });
                
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                }
                
                const data = await res.json();
                
                if (!cancelled) {
                    setFlags(data.flags || {});
                    setEnvironment(data.environment || 'local');
                    setError(null);
                }
            } catch (err) {
                if (!cancelled) {
                    const message = err instanceof Error ? err.message : 'Failed to fetch feature flags';
                    setError(message);
                    console.error('Feature flags fetch error:', err);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        fetchFlags();
        return () => { cancelled = true; };
    }, []);

    const value: FeatureFlagContextType = {
        isEnabled: (flag: string) => Boolean(flags[flag]),
        flags,
        environment,
        loading,
        error,
    };

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** Access the full feature-flag context. */
export const useFeatureFlags = (): FeatureFlagContextType => {
    const ctx = useContext(FeatureFlagContext);
    if (ctx === undefined) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
    }
    return ctx;
};

/** Convenience hook — returns true/false for a single flag. */
export const useFeatureFlag = (flag: string): boolean => {
    return useFeatureFlags().isEnabled(flag);
};

// ─── Gate component ──────────────────────────────────────────────────────────

interface FeatureGateProps {
    /** The flag name to check */
    flag: string;
    /** Content shown when the flag is ON */
    children: ReactNode;
    /** Optional content shown when the flag is OFF */
    fallback?: ReactNode;
}

/**
 * Conditionally renders children based on a feature flag.
 *
 * @example
 * ```tsx
 * <FeatureGate flag="athlete_story">
 *   <AthleteStorySection />
 * </FeatureGate>
 *
 * <FeatureGate flag="athlete_story" fallback={<ComingSoon />}>
 *   <AthleteStorySection />
 * </FeatureGate>
 * ```
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({ flag, children, fallback = null }) => {
    const enabled = useFeatureFlag(flag);
    return <>{enabled ? children : fallback}</>;
};
